// Smart Sash Admin Panel - Error Handler
// Centralized error handling for API calls

import { AxiosError } from 'axios';
import { ProblemDetails, ApiError } from '@/models';

/**
 * Extract error message from various error formats
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
};

/**
 * Check if error is an Axios error
 */
export const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as AxiosError).isAxiosError === true;
};

/**
 * Parse API error response into ApiError format
 */
export const parseApiError = (error: unknown): ApiError => {
  if (!isAxiosError(error)) {
    return {
      message: getErrorMessage(error),
    };
  }

  const axiosError = error as AxiosError;

  // Network error (no response)
  if (!axiosError.response) {
    return {
      message: 'Network error. Please check your connection.',
      code: 'NETWORK_ERROR',
    };
  }

  // Server returned error response
  const { status, data } = axiosError.response;

  // Try to parse ProblemDetails format
  if (data && typeof data === 'object') {
    const problemDetails = data as Partial<ProblemDetails>;

    if (problemDetails.title || problemDetails.detail) {
      return {
        message: problemDetails.detail || problemDetails.title || 'An error occurred',
        code: problemDetails.type || `HTTP_${status}`,
        details: problemDetails as ProblemDetails,
      };
    }

    // Try to extract message from other formats
    if ('message' in data && typeof data.message === 'string') {
      return {
        message: data.message,
        code: `HTTP_${status}`,
      };
    }

    // Try to extract validation errors
    if ('errors' in data && typeof data.errors === 'object') {
      return {
        message: 'Validation failed',
        code: 'VALIDATION_ERROR',
        validationErrors: data.errors as Record<string, string[]>,
      };
    }
  }

  // Default error messages by status code
  const defaultMessages: Record<number, string> = {
    400: 'Bad request. Please check your input.',
    401: 'Unauthorized. Please log in.',
    403: 'Forbidden. You do not have permission.',
    404: 'Resource not found.',
    409: 'Conflict. The resource already exists.',
    422: 'Validation failed. Please check your input.',
    429: 'Too many requests. Please try again later.',
    500: 'Internal server error. Please try again later.',
    502: 'Bad gateway. The server is unavailable.',
    503: 'Service unavailable. Please try again later.',
  };

  return {
    message: defaultMessages[status] || `Error ${status}: ${axiosError.message}`,
    code: `HTTP_${status}`,
  };
};

/**
 * Log error to console (in development)
 */
export const logError = (error: unknown, context?: string): void => {
  if (import.meta.env.DEV) {
    console.error(`[API Error${context ? ` - ${context}` : ''}]:`, error);
  }
};

/**
 * Handle API error and return formatted error
 */
export const handleApiError = (error: unknown, context?: string): ApiError => {
  logError(error, context);
  return parseApiError(error);
};
